import type { ApiClient } from '@carbonwebmail/api-client'
import type { CampaignRecipientDto, CampaignRecipientPageDto } from '@carbonwebmail/contracts'
import { useCallback, useState } from 'react'
import { usePaged } from '../../ui/usePaged.js'

/**
 * Who a campaign went to, one page at a time.
 *
 * The filter is part of the loader rather than something applied to the rows
 * here, for the reason the suppression list's is: the list is paged, and
 * filtering a page would answer "how many of these fifty" to a question about
 * forty thousand.
 *
 * Changing it goes back to page one. Page seven of "everybody" is not page
 * seven of "dead", and keeping the number would show an empty table over a
 * filter that has plenty of rows.
 */

export type RecipientFilter = CampaignRecipientDto['queue'] | 'all'

export function useCampaignRecipients({
  client,
  campaignId,
}: {
  readonly client: ApiClient
  readonly campaignId: string
}) {
  const [filter, setFilter] = useState<RecipientFilter>('all')

  const load = useCallback(
    (page: number) =>
      client.campaignRecipients(campaignId, page, filter === 'all' ? undefined : filter),
    [client, campaignId, filter],
  )

  const { state, setPage, retry } = usePaged<CampaignRecipientPageDto>(
    load,
    'We could not load the recipients of this campaign. Please try again in a moment.',
  )

  const narrow = useCallback(
    (next: RecipientFilter) => {
      setFilter(next)
      setPage(1)
    },
    [setPage],
  )

  return { filter, narrow, state, setPage, retry }
}
